/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Channel } from '../../../core/entities';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation';
import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { TOverlayOptions } from '../../../core/types';
import { BrandingUiUtility } from '../../../core/utils';
import { VideoPlaybackController } from '../../../core/videoplayer/VideoPlaybackController';
import { launchVodScreen } from '../../jumpchannel/jumpchannel.reducers';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { OverlayTypes } from '../OverlayTypes';

const TAG = 'jumpchanneloverlay.reducers';
const GLOBAL_MOUSE_TRAP = 'hideJumpChannelOverlay';
const JumpChannelOverlayAction = {
    GO_TO_NEXT_CHANNEL: 0,
    GO_TO_ON_DEMAND: 1,
};

export const showJumpChannelOverlay =
    (channel: Channel) => (dispatch: Function, getState: TGetState) => {
        const { isOverlayDialogActive } = getState().overlayDialog;
        if (isOverlayDialogActive) dispatch(hideOverlayDialog());

        const description = BrandingUiUtility.formatDescription(
            Locale.getString(LocaleKeys.JUMP_CHANNEL_OVERLAY_DESCRIPTION),
            {
                LAUNCH_TEXT: Locale.getString(LocaleKeys.GO_TO_ON_DEMAND_TEXT),
            },
        );
        const options: TOverlayOptions[] = [
            { text: Locale.getString(LocaleKeys.GO_TO_NEXT_CHANNEL_TEXT) },
            { text: Locale.getString(LocaleKeys.GO_TO_ON_DEMAND_TEXT) },
        ];
        const overlayProps: IOverlayDialog = {
            channel,
            description,
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options,
            title: Locale.getString(LocaleKeys.LEAVING_LIVE_TV_TEXT),
            overlayType: OverlayTypes.JUMP_CHANNEL,
        };

        dispatch(setScreenTaskController(ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS));
        dispatch(showOverlayDialog(overlayProps));
        Log.i(TAG, 'showJumpChannelOverlay', 'Displayed jump channel overlay');
    };

export const onKeyBackJumpChannelOverlay = () => (dispatch: Function) => {
    VideoPlaybackController.getInstance().playLastTunedChannel();
    dispatch(hideOverlayDialog());
    Log.i(TAG, 'onKeyBackJumpChannelOverlay', 'Hid jump channel overlay');
};

export const onKeyOkJumpChannelOverlay =
    (onKeyChannelUpVideoPlayerUi: Function) => (dispatch: Function, getState: TGetState) => {
        const { overlayHighlight, overlayDialog } = getState();
        const uiProps: IOverlayDialog = overlayDialog.uiProperties;

        switch (overlayHighlight.verticalPosition) {
            case JumpChannelOverlayAction.GO_TO_NEXT_CHANNEL:
                dispatch(onKeyChannelUpVideoPlayerUi());
                break;
            case JumpChannelOverlayAction.GO_TO_ON_DEMAND:
                dispatch(
                    launchVodScreen(uiProps.channel?.channelNumber ?? '', {
                        onSuccess: VideoPlaybackController.getInstance().playLastTunedChannel,
                    }),
                );
                break;
            default:
        }
        dispatch(hideOverlayDialog());
    };

export const onMouseClickJumpChannelOverlay =
    (onKeyChannelUpVideoPlayerUi: Function) => (dispatch: Function, getState: TGetState) => {
        const { value } = getState().inputFactory;

        if (value === GLOBAL_MOUSE_TRAP) {
            dispatch(onKeyBackJumpChannelOverlay());
            return;
        }
        dispatch(onKeyOkJumpChannelOverlay(onKeyChannelUpVideoPlayerUi));
    };
